import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import {
  apiPutAlertsPatientIdWaitingRoom,
  apiPutPatientsPatientId,
  PutApiV1AlertsByPatientIdWaitingRoomData,
  PutApiV1PatientsByPatientIdData,
} from "../../api";
import { showToast } from "../../components/ui-kit/toasts";

const CONCURRENCY = 4;

export type PatientBody = PutApiV1PatientsByPatientIdData["body"];
export type PatchEntry = { id: number; patch: PatientBody };

type WaitingRoomBody = PutApiV1AlertsByPatientIdWaitingRoomData["body"];

const WAITING_ROOM_KEYS = new Set<string>([
  "patient_state",
  "medical_monitoring_note",
  "technical_monitoring_note",
  "monitoring_watch_kind",
  "monitoring_watch_status",
  "monitoring_call_status",
  "monitoring_report_status",
] satisfies Array<keyof WaitingRoomBody>);

/**
 * Fan-out per-patient PUTs at a fixed concurrency, then surface results via toast.
 * Returns when every PUT has settled. Failed patient IDs are listed in the error toast
 * and returned so the caller can keep their drafts in place for retry.
 */
export async function bulkUpdatePatients(
  patches: Array<PatchEntry>,
): Promise<{ failedIds: number[] }> {
  if (patches.length === 0) return { failedIds: [] };

  const queue = [...patches];
  const failures: number[] = [];

  const runOne = async (entry: PatchEntry) => {
    try {
      if (Object.keys(entry.patch).every((key) => WAITING_ROOM_KEYS.has(key))) {
        await apiPutAlertsPatientIdWaitingRoom({
          path: { patient_id: entry.id },
          body: entry.patch,
        });
      } else {
        // Fields outside the waiting-room schema (e.g. region) still need the
        // generic patient update and its stricter permissions.
        await apiPutPatientsPatientId({
          path: { patient_id: entry.id },
          body: entry.patch,
        });
      }
    } catch {
      failures.push(entry.id);
    }
  };

  const workers = Array.from(
    { length: Math.min(CONCURRENCY, queue.length) },
    async () => {
      while (queue.length > 0) {
        const next = queue.shift();
        if (next !== undefined) await runOne(next);
      }
    },
  );
  await Promise.all(workers);

  const total = patches.length;
  if (failures.length === 0) {
    showToast({
      message: i18n._(msg`Updated ${total} patient(s)`),
      variant: "info",
    });
  } else {
    const succeeded = total - failures.length;
    const failed = failures.join(", ");
    showToast({
      message: i18n._(
        msg`Updated ${succeeded} of ${total}. Failed: ${failed}.`,
      ),
    });
  }

  return { failedIds: failures };
}
